import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FolderKanban, ChevronRight, ChevronDown, Building2, Pencil } from 'lucide-react';
import { useProjectStore } from '../../store/useProjectStore';
import { useAppStore } from '../../store/useAppStore';
import { useConfigStore } from '../../store/useConfigStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import CreateProjectModal from './CreateProjectModal';
import type { Project } from '../../types';

export default function ProjectsPanel() {
  const { projects } = useProjectStore();
  const { setSelectedProject, setActivePanel } = useAppStore();
  const { getHealthColor } = useConfigStore();
  const { canCreateProject } = useAuthStore();
  const { organizations, fetchOrganizations, fetchPrograms, fetchReleaseTrains } = useOrgHierarchyStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [groupByHierarchy, setGroupByHierarchy] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['all']));

  useEffect(() => {
    fetchOrganizations();
    fetchPrograms();
    fetchReleaseTrains();
  }, [fetchOrganizations, fetchPrograms, fetchReleaseTrains]);

  const filtered = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (projectId: string) => {
    setSelectedProject(projectId);
    setActivePanel(null);
    navigate(`/project/${projectId}`);
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const renderProjectItem = (project: Project) => (
    <div
      key={project._id}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors group"
    >
      <button
        onClick={() => handleSelect(project._id)}
        className="flex items-center gap-3 flex-1 min-w-0"
      >
        <div
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: getHealthColor(project.health) }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {project.displayId && <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mr-1">{project.displayId}</span>}
            {project.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{project.status}</p>
        </div>
        {project.stats && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {project.stats.completion}%
          </span>
        )}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); setEditProject(project); }}
        className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
        title="Edit project"
      >
        <Pencil size={14} className="text-gray-400 dark:text-gray-500" />
      </button>
    </div>
  );

  const renderGrouped = () => {
    // Group: Org → projects with that org (flat — no program/train nesting in sidebar for simplicity)
    const byOrg = new Map<string, Project[]>();
    const unassigned: Project[] = [];

    filtered.forEach((p) => {
      if (p.organizationId) {
        const list = byOrg.get(p.organizationId) || [];
        list.push(p);
        byOrg.set(p.organizationId, list);
      } else {
        unassigned.push(p);
      }
    });

    return (
      <div className="space-y-1">
        {organizations.map((org) => {
          const orgProjects = byOrg.get(org._id) || [];
          if (orgProjects.length === 0 && search) return null;
          const key = `org-${org._id}`;
          const isExpanded = expandedGroups.has(key);

          return (
            <div key={org._id}>
              <button
                onClick={() => toggleGroup(key)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-750 rounded"
              >
                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Building2 size={12} className="text-indigo-400" />
                <span className="truncate">{org.name}</span>
                <span className="ml-auto text-gray-400">{orgProjects.length}</span>
              </button>
              {isExpanded && (
                <div className="ml-2">
                  {orgProjects.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-1">No projects</p>
                  ) : (
                    orgProjects.map(renderProjectItem)
                  )}
                </div>
              )}
            </div>
          );
        })}

        {unassigned.length > 0 && (
          <div>
            <button
              onClick={() => toggleGroup('unassigned')}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-750 rounded"
            >
              {expandedGroups.has('unassigned') ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <FolderKanban size={12} className="text-gray-400" />
              <span>Unassigned</span>
              <span className="ml-auto text-gray-400">{unassigned.length}</span>
            </button>
            {expandedGroups.has('unassigned') && (
              <div className="ml-2">{unassigned.map(renderProjectItem)}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search + New */}
      <div className="p-3 space-y-2">
        {canCreateProject() && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 w-full px-3 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
          >
            <Plus size={16} />
            New Project
          </button>
        )}

        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
          />
        </div>

        {/* Group toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setGroupByHierarchy(!groupByHierarchy)}
            className={`text-xs px-2 py-1 rounded ${groupByHierarchy ? 'bg-accent/10 text-accent' : 'text-gray-400 hover:text-gray-600'}`}
          >
            {groupByHierarchy ? 'Grouped' : 'Flat'}
          </button>
        </div>
      </div>

      {/* Project List */}
      <div className="flex-1 overflow-y-auto px-2">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
            <FolderKanban size={32} className="mb-2" />
            <p className="text-sm">No projects found</p>
          </div>
        ) : groupByHierarchy && organizations.length > 0 ? (
          renderGrouped()
        ) : (
          <div className="space-y-0.5">
            {filtered.map(renderProjectItem)}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateProjectModal onClose={() => setShowCreate(false)} />
      )}

      {editProject && (
        <CreateProjectModal
          project={editProject}
          onClose={() => setEditProject(null)}
        />
      )}
    </div>
  );
}
